import { BusinessOutlined, EventOutlined, VpnKeyOutlined } from "@mui/icons-material";
import { Box, Stack, TextField } from "@mui/material";
import { COLORS } from "assets";
import { FormModalActions, FormRowSwitch, MyModal, SiteSelect, StyledInputLabel, TextInput } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { APITokenModel, MILLISECONDS } from "types";
import { validateName } from "utils";
import { dateFieldSx, fieldSx, iconSx } from "./formShared";

const initState = {
  name: "",
  siteId: "",
  date: new Date(Date.now() + MILLISECONDS.YEAR).toISOString().substring(0, 10)
};

interface IEdit {
  onSuccess?: () => void;
  onClose: () => void;
  token: APITokenModel;
}

export default function Edit({ onSuccess, onClose, token }: IEdit) {
  const [noExpiry, setNoExpiry] = useState(!Boolean(token.expiry));
  const [state, setState] = useState({
    name: token.name || "",
    siteId: token.siteId || "",
    date: token.expiry ? token.expiry.substring(0, 10) : initState.date
  });
  const [errors, setErrors] = useState({ name: "", date: "" });
  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchPut } = useFetch<{ _id: string }>("PUT");
  const { enqueueSnackbar } = useSnackbar();

  const disabledSubmit = useMemo(
    () => !!(errors.name || !state.name || !state.siteId || (!noExpiry && (!state.date || !!errors.date))),
    [errors, state, noExpiry]
  );

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      const body: Record<string, string | null> = {};
      if (state.siteId !== token.siteId) {
        body.siteId = state.siteId;
      }
      if (state.name !== token.name) {
        body.name = state.name;
      }
      if (noExpiry) {
        body.expiry = null;
      } else if (state.date !== (token.expiry ? token.expiry.substring(0, 10) : "")) {
        body.expiry = state.date;
      }
      if (Object.keys(body).length === 0) {
        enqueueSnackbar(t["Nothing to update"] || "Nothing to update", { variant: "info" });
        return;
      }
      callFetchPut(ENDPOINTS.APIToken + `/${token._id}`, {
        body: JSON.stringify(body)
      }).catch(console.error);
    },
    [state, token, noExpiry, callFetchPut, enqueueSnackbar, t]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess?.();
      onClose();
    }
  }, [response, onClose, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  return (
    <MyModal
      title={t["Edit API Token"]}
      subtitle={t["Edit API Token description"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <TextInput
            value={state.name}
            label={t["Name"]}
            type="text"
            placeholder={t["e.g API for Backend"]}
            error={!!errors.name}
            helperText={errors.name}
            startAdornment={<VpnKeyOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={(e) => {
              setState((prevState) => ({
                ...prevState,
                name: e.target.value
              }));
              setErrors((prevState) => ({
                ...prevState,
                name: validateName(e.target.value)
              }));
            }}
            fullWidth
            required
            autoFocus
          />

          <SiteSelect
            adminOnly
            value={state.siteId || undefined}
            startAdornment={<BusinessOutlined sx={iconSx} />}
            onChange={(siteId) => setState((prevState) => ({ ...prevState, siteId }))}
          />

          <Box>
            <StyledInputLabel>{t["Expiry Date"]}</StyledInputLabel>
            <TextField
              required={!noExpiry}
              disabled={noExpiry}
              value={state.date}
              type="date"
              placeholder="2025-10-10"
              error={!!errors.date}
              helperText={errors.date || " "}
              onChange={(e) => {
                setState((prevState) => ({
                  ...prevState,
                  date: e.target.value
                }));
                setErrors((prevState) => ({
                  ...prevState,
                  date: ""
                }));
              }}
              inputProps={{
                min: new Date(Date.now()).toISOString().substring(0, 10)
              }}
              fullWidth
              sx={dateFieldSx}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      pl: 1.5,
                      pr: 0.5,
                      display: "flex",
                      alignItems: "center",
                      color: COLORS.surface.secondaryText,
                      flexShrink: 0
                    }}
                  >
                    <EventOutlined sx={iconSx} />
                  </Box>
                )
              }}
            />
          </Box>

          <FormRowSwitch label={t["Do not Expire"]} checked={noExpiry} onChange={setNoExpiry} />
        </Stack>

        <FormModalActions
          onCancel={onClose}
          cancelLabel={t["Cancel"]}
          submitLabel={t["Update"]}
          loading={isLoading}
          disabled={disabledSubmit}
        />
      </Box>
    </MyModal>
  );
}
